/*
 * Recently Viewed Products.
 * Records the current product handle to localStorage on each product page view,
 * then renders the shopper's recent products (excluding the current one) by
 * fetching the dm-recently-viewed-card section per handle via the Section
 * Rendering API. The element stays hidden until at least one card is injected.
 *
 * The fetches (up to data-limit parallel section renders) are deferred until
 * the section nears the viewport — it sits at the bottom of the PDP, and firing
 * them at hydration time competed with product-info/global.js and the server.
 */
class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    const KEY = 'dm:recently-viewed';
    const MAX_STORED = 20;

    let handles = [];
    try {
      handles = JSON.parse(localStorage.getItem(KEY)) || [];
    } catch (e) {
      handles = [];
    }

    // Always record the current view, even if we never scroll far enough to render.
    const current = this.dataset.currentHandle;
    if (current) {
      handles = [current].concat(handles.filter((h) => h !== current)).slice(0, MAX_STORED);
      try {
        localStorage.setItem(KEY, JSON.stringify(handles));
      } catch (e) {
        /* storage full / disabled — non-fatal */
      }
    }

    const limit = parseInt(this.dataset.limit || '4', 10);
    this.toShow = handles.filter((h) => h && h !== current).slice(0, limit);
    if (!this.toShow.length) return;

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.io.disconnect();
            this.io = null;
            this.renderCards();
          }
        },
        { rootMargin: '400px 0px' }
      );
      // The element is [hidden] until cards land, so observe the parent section,
      // which always has layout.
      this.io.observe(this.closest('.shopify-section') || this.parentElement || this);
    } else {
      this.renderCards();
    }
  }

  renderCards() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const grid = this.querySelector('.dm-recently-viewed__grid');
    if (!grid) return;

    const section = this.dataset.cardSection;
    const parser = new DOMParser();

    Promise.all(
      this.toShow.map((handle) =>
        fetch(`${root}products/${handle}?section_id=${section}`)
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => ''))
    ).then((parts) => {
      const frag = document.createDocumentFragment();
      parts.forEach((text) => {
        if (!text) return;
        const doc = parser.parseFromString(text, 'text/html');
        // Pull the card item out regardless of any section wrapper the API adds.
        const item = doc.querySelector('.dm-recently-viewed__item') || doc.querySelector('.card-wrapper');
        if (item) frag.appendChild(item);
      });
      if (frag.childNodes.length) {
        grid.appendChild(frag);
        this.hidden = false;
      }
    });
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
